import
    {
        AutoAwesome,
        CheckCircle,
        ExpandLess,
        ExpandMore,
        Person,
        Psychology,
        School,
        Send,
        Star,
        WorkOutline,
    } from '@mui/icons-material';
import { Alert, Avatar, Box, Button, Chip, Collapse, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import SkillTileWidget from '../../Widgets/SkillTileWidget';
import type { OnboardingData } from '../types/OnboardingTypes';

interface OverviewStepProps
{
    data: OnboardingData;
    onNext: () => void;
    onBack: () => void;
    setAgentMessage: (message: string) => void;
}

export default function OverviewStep({ data, onNext, onBack, setAgentMessage }: OverviewStepProps)
{
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [skillsOpen, setSkillsOpen] = useState(true);
    const [certificatesOpen, setCertificatesOpen] = useState(true);
    const [rolesOpen, setRolesOpen] = useState(true);
    const [superPowersOpen, setSuperPowersOpen] = useState(true);

    const employee = useSelector((state: AppState) => state.employee);
    const companyName = useSelector((state: AppState) => state.company.name);
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang);

    useEffect(() =>
    {
        setAgentMessage(langStrings.onboarding.agentOverviewMessage);

        // Validate data
        const errors: string[] = [];

        if (!(data.personalInfo.firstName ?? '').trim())
        {
            errors.push(langStrings.onboarding.firstNameRequired);
        }
        if (!(data.personalInfo.lastName ?? '').trim())
        {
            errors.push(langStrings.onboarding.lastNameRequired);
        }
        if (!(data.personalInfo.shortDescription ?? '').trim())
        {
            errors.push(langStrings.onboarding.shortDescriptionRequired);
        }
        if (data.selectedItems.skills.length === 0)
        {
            errors.push(langStrings.onboarding.skillsRequired);
        }

        setValidationErrors(errors);
    }, [data, setAgentMessage]);

    return (
        <Box sx={{ maxWidth: isMobile ? '100%' : 1000, mx: 'auto', p: isMobile ? 0 : undefined }}>
            <Typography
                variant={isMobile ? 'h5' : 'h4'}
                sx={{ fontWeight: 600, mb: 1, fontSize: isMobile ? '1.25rem' : undefined }}
            >
                {langStrings.onboarding.overviewTitle}
            </Typography>
            <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: isMobile ? 2 : 4, fontSize: isMobile ? '0.9rem' : undefined }}
            >
                {langStrings.onboarding.overviewSubtitle}
            </Typography>

            {/* Validation Alerts */}
            {validationErrors.length > 0 && (
                <Alert severity="warning" sx={{ mb: 4 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {langStrings.onboarding.validationIncompleteTitle}
                    </Typography>
                    {validationErrors.map((error, index) => (
                        <Typography key={index} variant="body2">
                            • {error}
                        </Typography>
                    ))}
                </Alert>
            )}

            <Box sx={{ mb: 4 }}>
                {/* Personal Info Header */}
                <Box
                    sx={{
                        background: theme.palette.primaryToSecondaryGradient,
                        border: `1px solid ${theme.palette.primary}`,
                        overflow: 'hidden',
                        borderRadius: 3,
                        boxShadow: 2,
                        mb: 2,
                    }}
                >
                    <Box sx={{ px: 2, pb: 2, mt: 1 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                alignItems: isMobile ? 'center' : 'flex-start',
                                gap: 3,
                                p: 2,
                            }}
                        >
                            <Box sx={{ position: 'relative' }}>
                                <Avatar
                                    sx={{
                                        width: isMobile ? 140 : 100,
                                        height: isMobile ? 140 : 100,
                                        fontSize: isMobile ? '1rem' : '1.5rem',
                                        fontWeight: 400,
                                        bgcolor: theme.palette.primary.main,
                                        border: `2px solid ${theme.palette.secondary.main}`,
                                    }}
                                >
                                    {data.personalInfo.firstName.charAt(0)}
                                    {data.personalInfo.lastName.charAt(0)}
                                </Avatar>
                            </Box>

                            <Box sx={{ flex: 1, textAlign: isMobile ? 'center' : 'left', minWidth: 0 }}>
                                <Typography
                                    variant={isMobile ? 'h1' : 'h2'}
                                    sx={{
                                        fontWeight: 500,
                                        mb: 1,
                                        wordBreak: 'break-word',
                                        color: theme.palette.primary.contrastText,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: isMobile ? 'center' : 'flex-start',
                                        gap: 0.5,
                                    }}
                                >
                                    {data.personalInfo.firstName} {data.personalInfo.lastName}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: theme.palette.secondary.main,
                                        mb: 2.5,
                                        fontWeight: 400,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: isMobile ? 'center' : 'flex-start',
                                        flexDirection: isMobile ? 'column' : 'row',
                                        gap: isMobile ? 0.5 : 1,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {companyName || 'Skill-App GmbH'}
                                    </Box>
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.palette.primary.contrastText,
                                        opacity: 0.9,
                                        textAlign: isMobile ? 'center' : 'left',
                                    }}
                                >
                                    {data.personalInfo.shortDescription}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* SuperPowers Section */}
                {data.selectedItems.superPowers && data.selectedItems.superPowers.length > 0 && (
                    <Paper
                        sx={{
                            mb: 2,
                            overflow: 'hidden',
                            border: `2px solid ${theme.palette.warning.main}40`,
                            position: 'relative',
                        }}
                    >
                        <Box
                            sx={{
                                p: 2,
                                pt: 3,
                                cursor: 'pointer',
                                background: `linear-gradient(135deg, ${theme.palette.warning.main}08 0%, ${theme.palette.warning.light}05 100%)`,
                                '&:hover': { bgcolor: `${theme.palette.warning.main}15` },
                            }}
                            onClick={() => setSuperPowersOpen(!superPowersOpen)}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ bgcolor: theme.palette.warning.main, width: 32, height: 32 }}>
                                        <Star sx={{ fontSize: 18 }} />
                                    </Avatar>
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 600, color: theme.palette.warning.dark }}
                                    >
                                        {langStrings.onboarding.topSkills} ({data.selectedItems.superPowers.length})
                                    </Typography>
                                </Box>
                                {superPowersOpen ? <ExpandLess /> : <ExpandMore />}
                            </Box>
                        </Box>
                        <Collapse in={superPowersOpen}>
                            <Box sx={{ px: 2, pb: 3, pt: 2 }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                    {data.selectedItems.superPowers.map((skill: any) => (
                                        <Box
                                            key={skill.id}
                                            sx={{
                                                flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' },
                                                minWidth: 250,
                                                px: 1,
                                            }}
                                        >
                                            <SkillTileWidget
                                                id={skill.id}
                                                name={skill.name}
                                                level={skill.level}
                                                category={skill.category || 'Super Power'}
                                                type="skill"
                                                showStar={true}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Collapse>
                    </Paper>
                )}

                {/* Skills Section */}
                <Paper
                    sx={{
                        bgcolor: 'background.paper',
                        borderRadius: theme.shape.borderRadius,
                        mb: 2,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.primary.main}20`,
                    }}
                >
                    <Box
                        sx={{
                            p: 2,
                            cursor: 'pointer',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.main}05 100%)`,
                            '&:hover': { bgcolor: `${theme.palette.primary.main}15` },
                        }}
                        onClick={() => setSkillsOpen(!skillsOpen)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                                    <Psychology sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {langStrings.onboarding.skills} ({data.selectedItems.skills.length})
                                </Typography>
                            </Box>
                            {skillsOpen ? <ExpandLess /> : <ExpandMore />}
                        </Box>
                    </Box>
                    <Collapse in={skillsOpen}>
                        <Box sx={{ p: 0 }}>
                            {data.selectedItems.skills.map((skill: any) =>
                            {
                                const getLevelColor = (level: number) =>
                                {
                                    if (level <= 1) return theme.palette.error.main;
                                    if (level <= 2) return theme.palette.warning.main;
                                    if (level <= 3) return theme.palette.info.main;
                                    return theme.palette.success.main;
                                };
                                const getLevelName = (level: number) =>
                                {
                                    switch (level)
                                    {
                                        case 1:
                                            return langStrings.onboarding.junior;
                                        case 2:
                                            return langStrings.onboarding.midLevel;
                                        case 3:
                                            return langStrings.onboarding.senior;
                                        case 4:
                                            return langStrings.onboarding.expert;
                                        default:
                                            return langStrings.onboarding.junior;
                                    }
                                };
                                return (
                                    <Box
                                        key={skill.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            p: 2,
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                            '&:last-child': { borderBottom: 'none' },
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                                {skill.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {skill.category || langStrings.onboarding.other}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <Chip
                                                label={getLevelName(skill.level)}
                                                size="small"
                                                sx={{
                                                    bgcolor: getLevelColor(skill.level),
                                                    color: 'white',
                                                    fontWeight: 500,
                                                }}
                                            />
                                            <Chip
                                                label={
                                                    skill.source === 'user'
                                                        ? langStrings.onboarding.added
                                                        : langStrings.onboarding.aiAnalysis
                                                }
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    borderColor:
                                                        skill.source === 'user'
                                                            ? theme.palette.primary.main
                                                            : theme.palette.info.main,
                                                    color:
                                                        skill.source === 'user'
                                                            ? theme.palette.primary.main
                                                            : theme.palette.info.main,
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Collapse>
                </Paper>

                {/* Certificates Section */}
                <Paper
                    sx={{
                        bgcolor: 'background.paper',
                        borderRadius: theme.shape.borderRadius,
                        mb: 2,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.primary.main}20`,
                    }}
                >
                    <Box
                        sx={{
                            p: 2,
                            cursor: 'pointer',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.main}05 100%)`,
                            '&:hover': { bgcolor: `${theme.palette.primary.main}15` },
                        }}
                        onClick={() => setCertificatesOpen(!certificatesOpen)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                                    <School sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {langStrings.onboarding.certificates} ({data.selectedItems.certificates.length})
                                </Typography>
                            </Box>
                            {certificatesOpen ? <ExpandLess /> : <ExpandMore />}
                        </Box>
                    </Box>
                    <Collapse in={certificatesOpen}>
                        <Box sx={{ p: 0 }}>
                            {data.selectedItems.certificates.length > 0 ? (
                                data.selectedItems.certificates.map((cert: any) =>
                                {
                                    const getMockLogo = (certName: string) =>
                                    {
                                        const name = certName.toLowerCase();
                                        if (name.includes('aws')) return '/certificates/aws.png';
                                        if (name.includes('react')) return '/certificates/react.png';
                                        if (name.includes('scrum')) return '/certificates/scrum.png';
                                        if (name.includes('microsoft')) return '/certificates/microsoft.png';
                                        if (name.includes('google')) return '/certificates/google.png';
                                        return '/certificates/default.png';
                                    };

                                    return (
                                        <Box
                                            key={cert.id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                p: 2,
                                                borderBottom: `1px solid ${theme.palette.divider}`,
                                                '&:last-child': { borderBottom: 'none' },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    flex: 1,
                                                    minWidth: 0,
                                                }}
                                            >
                                                <Avatar
                                                    src={getMockLogo(cert.name)}
                                                    sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}
                                                >
                                                    <School sx={{ fontSize: 18 }} />
                                                </Avatar>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                                        {cert.name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {langStrings.onboarding.certificate}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <Chip
                                                    label={
                                                        cert.source === 'user'
                                                            ? langStrings.onboarding.added
                                                            : langStrings.onboarding.aiAnalysis
                                                    }
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        borderColor:
                                                            cert.source === 'user'
                                                                ? theme.palette.primary.main
                                                                : theme.palette.info.main,
                                                        color:
                                                            cert.source === 'user'
                                                                ? theme.palette.primary.main
                                                                : theme.palette.info.main,
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    );
                                })
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                    {langStrings.onboarding.noCertificatesSelected}
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </Paper>

                {/* Roles Section */}
                <Paper
                    sx={{
                        bgcolor: 'background.paper',
                        borderRadius: theme.shape.borderRadius,
                        mb: 2,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.primary.main}20`,
                    }}
                >
                    <Box
                        sx={{
                            p: 2,
                            cursor: 'pointer',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.main}05 100%)`,
                            '&:hover': { bgcolor: `${theme.palette.primary.main}15` },
                        }}
                        onClick={() => setRolesOpen(!rolesOpen)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                                    <WorkOutline sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {langStrings.onboarding.roles} ({data.selectedItems.roles.length})
                                </Typography>
                            </Box>
                            {rolesOpen ? <ExpandLess /> : <ExpandMore />}
                        </Box>
                    </Box>
                    <Collapse in={rolesOpen}>
                        <Box sx={{ p: 0 }}>
                            {data.selectedItems.roles.length > 0 ? (
                                data.selectedItems.roles.map((role: any) => (
                                    <Box
                                        key={role.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            p: 2,
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                            '&:last-child': { borderBottom: 'none' },
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                                {role.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {langStrings.onboarding.role}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <Chip
                                                label={
                                                    role.source === 'user'
                                                        ? langStrings.onboarding.added
                                                        : langStrings.onboarding.aiAnalysis
                                                }
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    borderColor:
                                                        role.source === 'user'
                                                            ? theme.palette.primary.main
                                                            : theme.palette.info.main,
                                                    color:
                                                        role.source === 'user'
                                                            ? theme.palette.primary.main
                                                            : theme.palette.info.main,
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                    {langStrings.onboarding.noRolesSelected}
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </Paper>
            </Box>

            {/* Navigation */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 4,
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 2 : 0,
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onBack}
                    sx={{ px: isMobile ? 2 : 3, fontSize: isMobile ? '0.875rem' : undefined }}
                >
                    {langStrings.onboarding.backToEdit}
                </Button>

                <Button
                    variant="contained"
                    size="medium"
                    onClick={onNext}
                    disabled={validationErrors.length > 0}
                    startIcon={<Send />}
                    sx={{
                        px: isMobile ? 2 : 4,
                        py: isMobile ? 1 : 1.5,
                        fontSize: isMobile ? '0.875rem' : '1.1rem',
                        fontWeight: 600,
                        background:
                            validationErrors.length === 0
                                ? `linear-gradient(45deg, ${theme.palette.success.main} 30%, ${theme.palette.primary.main} 90%)`
                                : undefined,
                        boxShadow:
                            validationErrors.length === 0
                                ? `0 8px 32px ${alpha(theme.palette.success.main, 0.3)}`
                                : undefined,
                        '&:hover':
                            validationErrors.length === 0
                                ? {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 12px 40px ${alpha(theme.palette.success.main, 0.4)}`,
                                }
                                : {},
                        transition: 'all 0.3s ease',
                    }}
                >
                    {langStrings.onboarding.completeOnboarding}
                </Button>
            </Box>
        </Box>
    );
}
